import { z } from "zod";
import { widgetBrandingSchema } from "./config";

/**
 * Faza 5 (optimizare costuri): selectorii se calculează determinist în
 * browser (SDK, aceeași logică ca picker-ul manual de elemente), nu de LLM.
 * SDK-ul trimite lista de elemente interactive candidate; backend-ul cere
 * LLM-ului doar să aleagă din `ref`-uri și să atribuie semantică — niciodată
 * să scrie CSS. Contract definit o singură dată aici, folosit de SDK (la
 * trimitere) și backend (la validare), ca protocolul să nu diverge.
 */
export const domElementRefSchema = z.object({
  ref: z.string().max(50),
  selector: z.string().max(500),
  tag: z.string().max(30),
  text: z.string().max(300).optional(),
  attrs: z.record(z.string(), z.string()).optional(),
});

/**
 * Brandingul detectat de SDK din pagina scanată (sdk/builder/brand.ts): culoarea
 * dominantă a sitului + logo-ul lui. Aceleași constrângeri ca la setarea manuală
 * (widgetBrandingSchema) — inclusiv refuzul non-http(s) pe logoUrl (S-16) —
 * fiindcă valorile ajung în același `apps.widget_branding`.
 */
export const detectedBrandSchema = z.object({
  primaryColor: widgetBrandingSchema.shape.primaryColor,
  logoUrl: widgetBrandingSchema.shape.logoUrl,
});

export const readPageRequestSchema = z.object({
  path: z.string().min(1).max(500),
  title: z.string().max(300).optional(),
  dom: z.string().min(1).max(300000),
  elements: z.array(domElementRefSchema).max(300).default([]),
  /** true = citire declanșată manual de site owner (buton), fals/absent = citire automată din scanarea sitului. */
  manual: z.boolean().default(false),
  /** URL absolut al <link rel="icon"> citit din <head>, dacă situl declară unul (vezi sdk/builder/dom.ts). */
  favicon: z.string().url().max(2000).optional(),
  /** Branding detectat din pagină (culoare + logo) — backend-ul completează DOAR câmpurile goale din widgetBranding. */
  brand: detectedBrandSchema.optional(),
});

/** Trimis de SDK la finalul auto-scanării: backend-ul compune și persistă mesajul de kickoff al interviului. */
export const scanCompleteRequestSchema = z.object({
  pagesRead: z.number().int().min(0).max(500),
  pagesSkipped: z.number().int().min(0).max(10000),
});

export type DomElementRef = z.infer<typeof domElementRefSchema>;
export type DetectedBrand = z.infer<typeof detectedBrandSchema>;
export type ReadPageRequest = z.infer<typeof readPageRequestSchema>;
export type ScanCompleteRequest = z.infer<typeof scanCompleteRequestSchema>;
